import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import UsersAPI from '../../UserContainer/user-api';
import PillarAPI from '../../PillarContainer/pillar-api';
import { TODAY } from '../../../utils/constants';
import { AVAILABLE_HOURS } from '../../../utils/dates';
import { AppContext } from '../../../app/AppContext';

const SESSION_QTY = Array.from({ length: 15 }, (_, index) => {
  const qty = index + 1;
  const word = qty > 1 ? 'veces' : 'vez';
  return { id: qty, value: `${qty} ${word}` };
});

const DAYS_BETWEEN = Array.from({ length: 15 }, (_, index) => {
  const qty = index + 1;
  const word = qty > 1 ? 'días' : 'día';
  return { id: qty, value: `${qty} ${word}` };
});

const getMentorName = (mentor) =>
  mentor.name ||
  [mentor.firstName, mentor.lastNames].filter(Boolean).join(' ') ||
  'Mentor sin nombre';

const getPillarId = (pillar) => {
  const value = pillar && typeof pillar === 'object' ? pillar.id : pillar;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (!/^\d+$/.test(String(value).trim())) return null;

  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const FormValuesObserver = ({ values, onChange }) => {
  const previousValues = useRef(values);

  useEffect(() => {
    if (previousValues.current !== values && onChange) onChange();
    previousValues.current = values;
  }, [onChange, values]);

  return null;
};

const SectionHeader = ({ icon: Icon, eyebrow, title, description, id }) => (
  <header className="session-scheduler__card-header">
    <span className="session-scheduler__section-icon" aria-hidden="true">
      <Icon />
    </span>
    <div className="session-scheduler__card-copy">
      <span className="session-scheduler__eyebrow">{eyebrow}</span>
      <h2 id={id}>{title}</h2>
      <p>{description}</p>
    </div>
  </header>
);

const FieldLabel = ({ name, children, required = true }) => (
  <label
    className="session-scheduler__field-label"
    htmlFor={name}
    id={`${name}-label`}
  >
    {children}
    {required && (
      <span className="session-scheduler__required" aria-hidden="true">
        {' '}*
      </span>
    )}
  </label>
);

const SchedulerSelect = ({
  name,
  label,
  options,
  placeholder,
  formik,
  disabled,
  hint,
}) => {
  const error = formik.touched[name] && formik.errors[name];

  return (
    <div className="session-scheduler__field">
      <FieldLabel name={name}>{label}</FieldLabel>
      <TextField
        className="session-scheduler__input"
        id={name}
        name={name}
        select
        variant="outlined"
        margin="none"
        fullWidth
        required
        disabled={disabled}
        value={formik.values[name] || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(error)}
        helperText={error || hint}
        SelectProps={{
          displayEmpty: true,
          labelId: `${name}-label`,
          MenuProps: {
            PaperProps: { className: 'session-scheduler-options' },
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          },
          renderValue: (value) =>
            options.find((option) => String(option.id) === String(value))
              ?.value || (
              <span className="session-scheduler__placeholder">
                {placeholder}
              </span>
            ),
        }}
      >
        <MenuItem value="" disabled className="session-scheduler-option">
          {placeholder}
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            className="session-scheduler-option"
          >
            {option.value}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

const SessionForm = ({
  session,
  setSession,
  onFormChange,
  isBusy = false,
  isCheckingSchedules = false,
}) => {
  const { user } = useContext(AppContext);
  const isAdmin = user?.role?.type === 'admin';
  const { state = {} } = useLocation();
  const { clientId, ref, announcementId } = state;
  const [mentors, setMentors] = useState([]);
  const [allPillars, setAllPillars] = useState([]);
  const [optionsStatus, setOptionsStatus] = useState('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let isCurrentRequest = true;
    setOptionsStatus('loading');
    setMentors([]);
    setAllPillars([]);

    (async () => {
      try {
        const [mentorsResponse, pillarsResponse] = await Promise.all([
          UsersAPI.getMentors(clientId, ref, announcementId),
          PillarAPI.fetchActivePillars(),
        ]);

        if (!isCurrentRequest) return;

        setMentors(mentorsResponse.data || []);
        setAllPillars(pillarsResponse.data || []);
        setOptionsStatus('ready');
      } catch (error) {
        if (isCurrentRequest) setOptionsStatus('error');
      }
    })();

    return () => {
      isCurrentRequest = false;
    };
  }, [announcementId, clientId, loadAttempt, ref]);

  const getMentorPillarOptions = (mentorId) => {
    const selectedMentor = mentors.find(
      (mentor) => String(mentor.id) === String(mentorId)
    );
    if (!selectedMentor) return [];

    const allowedIds = String(selectedMentor.prePillars || '')
      .split(',')
      .map(getPillarId)
      .filter(Boolean);

    // Some registered mentors have only their current pillar populated.
    // Keep valid prePillars authoritative instead of adding extra choices.
    if (allowedIds.length === 0) {
      const currentPillarId = getPillarId(selectedMentor.pillar);
      if (currentPillarId) allowedIds.push(currentPillarId);
    }

    return allPillars
      .filter((pillar) => allowedIds.includes(Number(pillar.id)))
      .map((pillar) => ({ id: pillar.id, value: pillar.name }));
  };

  const schema = Yup.object().shape({
    mentorId: Yup.string().required('Debes seleccionar un mentor'),
    pillarId: Yup.string().required('Debes seleccionar un pilar'),
    menteeId: Yup.string().required('Debes seleccionar un mentee'),
    title: Yup.string().trim().required('Debes ingresar un asunto'),
    description: Yup.string()
      .trim()
      .required('Debes ingresar una descripción'),
    startDay: Yup.date()
      .typeError('Debes ingresar una fecha válida')
      .required('Debes ingresar una fecha de inicio'),
    startTime: Yup.number()
      .min(8, 'Debes ingresar una hora válida')
      .required('Debes ingresar una hora'),
    timesToRepeat: Yup.number()
      .min(1, 'Debes seleccionar la cantidad de reuniones')
      .required('Debes seleccionar la cantidad de reuniones'),
    daysToRepeat: Yup.number()
      .min(1, 'Debes seleccionar la cantidad de días entre reuniones')
      .required('Debes seleccionar la cantidad de días entre reuniones'),
  });

  const handleSubmit = (values) => {
    if (isBusy || isCheckingSchedules || optionsStatus !== 'ready') return;

    const selectedMentor = mentors.find(
      (mentor) => String(mentor.id) === String(values.mentorId)
    );
    const selectedPillar = allPillars.find(
      (pillar) => String(pillar.id) === String(values.pillarId)
    );

    setSession({
      ...values,
      mentorName: selectedMentor ? getMentorName(selectedMentor) : '',
      pillarName: selectedPillar?.name || '',
    });
  };

  return (
    <Formik
      initialValues={session}
      validationSchema={schema}
      validateOnChange
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const mentorId = formik.values.mentorId;
        const selectedMentor =
          mentors.find((mentor) => String(mentor.id) === String(mentorId)) ||
          null;
        const mentorPillarOptions = mentorId
          ? getMentorPillarOptions(mentorId)
          : [];
        const mentorError = formik.touched.mentorId && formik.errors.mentorId;
        const fieldProps = (name) => ({
          id: name,
          name,
          value: formik.values[name] || '',
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          error: Boolean(formik.touched[name] && formik.errors[name]),
          helperText: formik.touched[name] && formik.errors[name],
          disabled: isBusy,
          required: true,
          variant: 'outlined',
          margin: 'none',
          fullWidth: true,
          className: 'session-scheduler__input',
        });

        return (
          <Form
            className="session-scheduler__form"
            id="session-scheduler-form"
            noValidate
            aria-busy={isBusy}
          >
            <FormValuesObserver
              values={formik.values}
              onChange={onFormChange}
            />
            <section
              className="session-scheduler__card"
              aria-labelledby="session-participants-title"
            >
              <SectionHeader
                id="session-participants-title"
                icon={PeopleOutlineIcon}
                eyebrow="PERSONAS"
                title="Participantes"
                description="Selecciona al mentor y el área de trabajo para estas reuniones."
              />
              <div className="session-scheduler__fields">
                <div className="session-scheduler__field session-scheduler__field--wide">
                  <FieldLabel name="mentorName">Mentor</FieldLabel>
                  <Autocomplete
                    id="mentorName"
                    options={mentors}
                    value={selectedMentor}
                    getOptionLabel={getMentorName}
                    getOptionSelected={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    disabled={isBusy || optionsStatus !== 'ready'}
                    loading={optionsStatus === 'loading'}
                    loadingText="Cargando mentores…"
                    noOptionsText="No se encontraron mentores"
                    openText="Mostrar mentores"
                    closeText="Cerrar opciones"
                    clearText="Quitar mentor"
                    classes={{
                      paper: 'session-scheduler-options',
                      option: 'session-scheduler-option',
                    }}
                    onChange={(event, option) => {
                      formik.setFieldValue('mentorId', option?.id || '');
                      formik.setFieldValue('pillarId', '');
                      formik.setFieldTouched('mentorId', true);
                      formik.setFieldTouched('pillarId', false);
                    }}
                    onBlur={() => formik.setFieldTouched('mentorId', true)}
                    renderOption={(option) => (
                      <>
                        <span
                          className="session-scheduler__mentor-option-avatar"
                          aria-hidden="true"
                        >
                          {getMentorName(option)
                            .split(' ')
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((part) => part.charAt(0))
                            .join('')
                            .toUpperCase()}
                        </span>
                        <span className="session-scheduler__mentor-option-copy">
                          {getMentorName(option)}
                        </span>
                      </>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="session-scheduler__input"
                        variant="outlined"
                        margin="none"
                        fullWidth
                        required
                        placeholder={
                          optionsStatus === 'loading'
                            ? 'Cargando mentores…'
                            : 'Busca y selecciona un mentor'
                        }
                        error={Boolean(mentorError)}
                        helperText={mentorError || undefined}
                        inputProps={{
                          ...params.inputProps,
                          'aria-labelledby': 'mentorName-label',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {optionsStatus === 'loading' && (
                                <CircularProgress
                                  size={18}
                                  aria-label="Cargando mentores"
                                />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {announcementId && (
                    <p className="session-scheduler__hint">
                      Solo se muestran mentores activos y aceptados en esta
                      convocatoria.
                    </p>
                  )}
                  {optionsStatus === 'ready' && mentors.length === 0 && (
                    <p className="session-scheduler__feedback" role="status">
                      {announcementId
                        ? 'Esta convocatoria aún no tiene mentores disponibles para agendar.'
                        : 'No hay mentores disponibles para agendar.'}
                    </p>
                  )}
                  {optionsStatus === 'error' && (
                    <div
                      className="session-scheduler__feedback session-scheduler__feedback--error"
                      role="alert"
                    >
                      <span>No pudimos cargar los mentores y sus pilares.</span>
                      <button
                        className="session-scheduler__retry"
                        type="button"
                        disabled={isBusy}
                        onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                      >
                        Reintentar
                      </button>
                    </div>
                  )}
                </div>
                <SchedulerSelect
                  name="pillarId"
                  label="Pilar del mentor"
                  options={mentorPillarOptions}
                  placeholder="Selecciona un pilar"
                  formik={formik}
                  disabled={
                    isBusy || !mentorId || mentorPillarOptions.length === 0
                  }
                  hint={
                    !mentorId
                      ? 'Primero selecciona un mentor.'
                      : mentorPillarOptions.length === 0
                      ? 'El mentor no tiene pilares disponibles.'
                      : undefined
                  }
                />
                <div className="session-scheduler__field">
                  <FieldLabel name="menteeName" required={false}>
                    Mentee
                  </FieldLabel>
                  <TextField
                    {...fieldProps('menteeName')}
                    required={false}
                    InputProps={{ readOnly: true }}
                    helperText={
                      announcementId
                        ? 'Emprendedor seleccionado desde la convocatoria.'
                        : 'Emprendedor seleccionado para estas reuniones.'
                    }
                  />
                </div>
              </div>
            </section>

            <section
              className="session-scheduler__card"
              aria-labelledby="session-content-title"
            >
              <SectionHeader
                id="session-content-title"
                icon={DescriptionOutlinedIcon}
                eyebrow="CONTENIDO"
                title="Información de la reunión"
                description="Define el asunto y los temas que trabajarán los participantes."
              />
              <div className="session-scheduler__fields">
                <div className="session-scheduler__field session-scheduler__field--wide">
                  <FieldLabel name="title">Asunto</FieldLabel>
                  <TextField
                    {...fieldProps('title')}
                    placeholder="Ej.: Plan comercial y próximos objetivos"
                  />
                </div>
                <div className="session-scheduler__field session-scheduler__field--wide">
                  <FieldLabel name="description">Descripción</FieldLabel>
                  <TextField
                    {...fieldProps('description')}
                    multiline
                    rows={4}
                    placeholder="Describe el objetivo y los temas de la reunión"
                  />
                </div>
              </div>
            </section>

            <section
              className="session-scheduler__card"
              aria-labelledby="session-schedule-title"
            >
              <SectionHeader
                id="session-schedule-title"
                icon={CalendarTodayOutlinedIcon}
                eyebrow="FECHAS Y FRECUENCIA"
                title="Programación"
                description="Elige el primer horario y la frecuencia. Luego revisa la disponibilidad."
              />
              <div className="session-scheduler__fields">
                <div className="session-scheduler__field">
                  <FieldLabel name="startDay">Fecha de inicio</FieldLabel>
                  <TextField
                    {...fieldProps('startDay')}
                    type="date"
                    inputProps={isAdmin ? undefined : { min: TODAY }}
                  />
                </div>
                <SchedulerSelect
                  name="startTime"
                  label="Hora de inicio"
                  options={AVAILABLE_HOURS}
                  placeholder="Selecciona una hora"
                  formik={formik}
                  disabled={isBusy}
                />
                <SchedulerSelect
                  name="timesToRepeat"
                  label="Cantidad de reuniones"
                  options={SESSION_QTY}
                  placeholder="Selecciona la cantidad"
                  formik={formik}
                  disabled={isBusy}
                />
                <SchedulerSelect
                  name="daysToRepeat"
                  label="Repetir cada"
                  options={DAYS_BETWEEN}
                  placeholder="Selecciona la frecuencia"
                  formik={formik}
                  disabled={isBusy}
                />
              </div>
              <div className="session-scheduler__form-actions">
                <p className="session-scheduler__hint">
                  Revisa los horarios antes de confirmar las reuniones.
                </p>
                <button
                  className="session-scheduler__button session-scheduler__button--primary"
                  type="submit"
                  disabled={
                    isBusy ||
                    isCheckingSchedules ||
                    optionsStatus !== 'ready' ||
                    mentors.length === 0
                  }
                >
                  {isCheckingSchedules ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <EventAvailableOutlinedIcon aria-hidden="true" />
                  )}
                  {isCheckingSchedules ? 'Consultando horarios…' : 'Ver horarios'}
                </button>
              </div>
            </section>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SessionForm;
